import {
  Box,
  Paper,
  Tab,
  Tabs,
  Stack,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import React from "react";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import TabPanelFormat from "../../../../components/widgets/TabPanelFormat";
import { getHashdata } from "./utils";
import { setHash } from "../../../../components/Tabs/Utils/hashFunctions";
import { pvTabs } from "./PVTabs";
import { PVContext } from "./PV.Context";
import LoadProjectDataComboBox from "./LoadProjectDataComboBox";
import { formatClientPrefix } from "../../../../utils/clientPrefix";
import useWbsUpdated, {
  usePlantUpdated,
} from "../../../../hooks/useWbsUpdated";
import { getPlantById, getAllData, getProjectById } from "./api";
import useEmsTreeAvailable from "../../../../hooks/useEmsTreeAvailable";
import { useGeneralContext } from "../../AllEquipments/CommonFeatures/SidePanel/GeneralContext";
import { usePVContext } from "./PressureVesselData/PVContext";
import EdgeMentorHost from "../../AllEquipments/CommonFeatures/Edge Mentor/EdgeMentorHost";
import { resolvePVEdgeMentorCodeLabel } from "./edgeMentorResolver";
import RevisionSaveStamp from "../../../../components/widgets/RevisionSaveStamp";
import {
  formatRevisionSaveDate,
  readRevisionSaveStamp,
} from "../../../../utils/revisionSaveStamp";
import useHashValue from "../../../../hooks/useHashValue";
import { findPvEstimationDoc } from "../../../../utils/pvEstimationDocs";
import { PV_ESTIMATION_SUB_HASH_KEY } from "../../../../components/Drawers/TreeViewDrawer/treeView/utils/estimationLinks";
import {
  getProjectDocumentList,
  resolveDocumentNameDescription,
} from "../../AllEquipments/CommonFeatures/DCSTab/Table/Dashboard/docListAPI";

const NEW_REVISION_OPTION = "__new";

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

// Mirrors PVContext.jsx's revision storage (flat inputData[rev] map) so the
// Rev. dropdown reflects the revisions actually saved for this project in
// the backend, rather than the FreeCAD-only revision list the old
// ModelingControls tool used.
function extractRevisionKeys(allData) {
  const bucket = isPlainObject(allData?.inputData) ? allData.inputData : {};

  const keys = Object.keys(bucket).filter((k) => !isNaN(Number(k)));
  if (!keys.includes("0")) keys.push("0");

  return keys.sort((a, b) => Number(a) - Number(b));
}

// The "saved by / saved at" stamp savePVData writes onto each revision, keyed
// by revision. Revisions saved before the stamp existed simply have none.
function extractRevisionStamps(allData) {
  const bucket = isPlainObject(allData?.inputData) ? allData.inputData : {};

  return Object.fromEntries(
    Object.entries(bucket)
      .filter(([key]) => !isNaN(Number(key)))
      .map(([key, value]) => [key, readRevisionSaveStamp(value)])
      .filter(([, stamp]) => Boolean(stamp)),
  );
}

const compactComboSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 18,
    height: 22,
    padding: "0 28px 0 8px !important",
    fontSize: 11.5,
  },
  "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
    padding: "0 !important",
    height: 22,
    lineHeight: "22px",
    fontSize: 11.5,
  },
  "& .MuiInputLabel-root": {
    fontSize: 11.5,
    transform: "translate(14px, 4px) scale(1)",
  },
  "& .MuiInputLabel-shrink": {
    transform: "translate(14px, -9px) scale(0.75)",
  },
  "& .MuiAutocomplete-endAdornment": {
    top: "50%",
    transform: "translateY(-50%)",
  },
};

// One column of the header. Every column reserves the same sort of width,
// which is what sets the even spacing across the row - so a field wide
// enough to hold an input (WBS UID at 260) has to drop back to this
// default once it is only reporting a value, or its unused width reads as
// a gap that the columns beside it do not have.
function HeaderField({ label, children, minWidth = 160 }) {
  return (
    <Stack spacing={0.25} sx={{ minWidth }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{
          whiteSpace: "nowrap",
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

/**
 * The Rev field's rows while a document tab is open: which revision this is
 * and when it was saved on one line, who saved it on the next - the document
 * the tab stands for having taken the field's label.
 *
 * Same two lines RevisionSaveStamp draws under an ordinary Rev field, re-cut
 * so the date sits with the revision number rather than under the name.
 */
function DocumentRevisionStamp({ revisionLabel, stamp }) {
  const savedAt = formatRevisionSaveDate(stamp?.savedAt);
  const revisionLine = [revisionLabel, savedAt].filter(Boolean).join(" — ");

  const lineSx = {
    display: "block",
    maxWidth: 200,
    lineHeight: 1.25,
    fontSize: 10.5,
  };

  return (
    <>
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        title={revisionLine}
        sx={lineSx}
      >
        {revisionLine}
      </Typography>
      {stamp?.savedBy && (
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          title={stamp.savedBy}
          sx={{ ...lineSx, fontWeight: 600 }}
        >
          {stamp.savedBy}
        </Typography>
      )}
    </>
  );
}

function ProjectSelectionHeader({
  plantId,
  clientName,
  type,
  selectedProject,
  onProjectSelect,
  activeTabLabel,
}) {
  const [plantName, setPlantName] = React.useState("");
  // Optional - only projects created with one (see the Add Project dialog in
  // EmsProjectsTreeView) have it, and it shows under the name when they do.
  const [plantDescription, setPlantDescription] = React.useState("");
  // The plant's client: short name first (its prefix - "CPR", "CDT", "PEI"),
  // full name under it.
  const [clientPrefix, setClientPrefix] = React.useState("");
  const [clientFullName, setClientFullName] = React.useState("");
  const [revisions, setRevisions] = React.useState(["0"]);
  // revision key -> { savedBy, savedAt }, shown under the Rev. picker.
  const [revisionStamps, setRevisionStamps] = React.useState({});

  // With the EMS tree on screen it is the single place a WBS/revision gets
  // picked, so the header only reports the selection - on every tab,
  // Design included.
  const treeOwnsSelection = useEmsTreeAvailable();

  const {
    selectedModelingRevision,
    setSelectedModelingRevision,
    setRevision,
    isDesignTabActive,
  } = useGeneralContext();

  // The project this WBS sits under, as edited in the projects tree: its
  // name is this header's "PROJECT# UID", its description the line beneath.
  usePlantUpdated(plantId, ({ changes }) => {
    if (changes.name !== undefined) setPlantName(changes.name);
    if (changes.description !== undefined) {
      setPlantDescription(changes.description);
    }
  });

  // The project's own code, without the client prefix the name carries
  // ("CPR-015" -> "015") - the Client UID field beside this one already
  // shows that prefix. Names that don't start with it are left alone.
  const projectCode = React.useMemo(() => {
    const prefix = clientPrefix ? `${clientPrefix}-` : "";
    if (!prefix || !plantName) return plantName;

    return plantName.toLowerCase().startsWith(prefix.toLowerCase())
      ? plantName.slice(prefix.length)
      : plantName;
  }, [plantName, clientPrefix]);

  const canEditSelection = isDesignTabActive && !treeOwnsSelection;
  const { confirmedRevision, setConfirmedRevision, savePVData } =
    usePVContext();

  // The dropdown only ever shows a revision as "selected" once it has
  // actually been confirmed (and thus loaded) for the currently selected
  // project - never a leftover value from a previously viewed project.
  const displayedRevision =
    confirmedRevision && confirmedRevision.projectId === selectedProject?.id
      ? confirmedRevision.revision
      : null;

  // Whichever revision the Rev. field is currently reporting - the picked one
  // when the header owns selection, the tree-driven one otherwise.
  const shownRevision = canEditSelection
    ? displayedRevision
    : (selectedModelingRevision ?? "0");

  const shownSaveStamp =
    shownRevision == null ? null : revisionStamps[String(shownRevision)] || null;

  // The revision number rides on the label ("REV. 0"), leaving the rows under
  // it for the picker and the saved-by stamp.
  const revisionLabel =
    shownRevision == null || shownRevision === ""
      ? "Rev."
      : `Rev. ${shownRevision}`;

  // ── The document behind the Estimation tab that is open ──────────────────
  // Main Estimation and Main BOM are each a document of this revision in the
  // Document Register (an Estimation Summary, a BOM - see pvEstimationDocs),
  // so while one of them is open the header reports that document the way it
  // reports the WBS and the revision: which one it is (Doc #) and which
  // revision OF IT this is.
  //
  // Which sub-tab is open is read from the hash rather than passed down:
  // those tabs are several levels below this header and write their
  // selection straight onto the URL (see NavTabs).
  const estimationSubTab = useHashValue(PV_ESTIMATION_SUB_HASH_KEY);
  const shownEstimationTab =
    activeTabLabel === "Estimation" &&
    (estimationSubTab === "Main Estimation" || estimationSubTab === "Main BOM")
      ? estimationSubTab
      : null;

  const [registerDocs, setRegisterDocs] = React.useState([]);
  const registerProjectId = shownEstimationTab ? selectedProject?.id : null;

  React.useEffect(() => {
    if (!registerProjectId) {
      setRegisterDocs([]);
      return undefined;
    }

    let cancelled = false;
    getProjectDocumentList("all", registerProjectId)
      .then((docs) => {
        if (!cancelled) setRegisterDocs(Array.isArray(docs) ? docs : []);
      })
      .catch(() => {
        if (!cancelled) setRegisterDocs([]);
      });

    return () => {
      cancelled = true;
    };
  }, [registerProjectId]);

  const estimationDoc = React.useMemo(
    () =>
      findPvEstimationDoc(registerDocs, {
        revision: shownRevision,
        tabLabel: shownEstimationTab,
      }),
    [registerDocs, shownRevision, shownEstimationTab],
  );

  // The client's own reference for that document on one line - their number
  // and their revision of it, both typed per row in the register and neither
  // to be confused with this WBS's revision above.
  const clientDocumentRef = [
    String(estimationDoc?.docRefNumber ?? "").trim(),
    String(estimationDoc?.docRevision ?? "").trim() !== ""
      ? `Rev ${estimationDoc.docRevision}`
      : "",
  ]
    .filter(Boolean)
    .join(" — ");

  // What the open document is called: the name of the identifier it was
  // created under in the register ("Estimation Summary", "Bill of
  // Materials"), falling back to the tab's own name while no document of
  // this revision has been filed for it yet.
  const shownDocumentName =
    (estimationDoc &&
      resolveDocumentNameDescription(
        estimationDoc.documentName ?? estimationDoc.uid ?? "",
      )) ||
    shownEstimationTab;

  const [saving, setSaving] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    severity: "success",
    message: "",
  });

  const handleSave = async () => {
    setSaving(true);

    try {
      const result = await savePVData();

      // Reflect the save immediately - the header would otherwise keep
      // showing the previous stamp until the project is reloaded.
      if (result?.saveStamp && result?.revision != null) {
        setRevisionStamps((prev) => ({
          ...prev,
          [String(result.revision)]: readRevisionSaveStamp(result.saveStamp),
        }));
      }

      setSnackbar({
        open: true,
        severity: result?.ok ? "success" : "warning",
        message: result?.ok
          ? `Saved revision ${selectedModelingRevision}`
          : "Save completed with some failures",
      });
    } catch (error) {
      console.error("Failed to save PV data", error);
      setSnackbar({
        open: true,
        severity: "error",
        message: error?.message || "Failed to save",
      });
    } finally {
      setSaving(false);
    }
  };

  React.useEffect(() => {
    if (!plantId) return;

    let cancelled = false;

    getPlantById(plantId)
      .then((plant) => {
        if (cancelled) return;
        setPlantName(plant?.name || "");
        setPlantDescription(plant?.description || "");
        setClientPrefix(formatClientPrefix(plant?.client?.prefix));
        setClientFullName(plant?.client?.name || "");
      })
      .catch((error) => {
        console.log("Failed to load plant", error);
      });

    return () => {
      cancelled = true;
    };
  }, [plantId]);

  React.useEffect(() => {
    if (!selectedProject?.id) {
      setRevisions(["0"]);
      setRevisionStamps({});
      return;
    }

    let cancelled = false;

    getAllData(selectedProject.id)
      .then((res) => {
        if (cancelled) return;

        const keys = extractRevisionKeys(res?.all);
        setRevisions(keys);
        setRevisionStamps(extractRevisionStamps(res?.all));

        // Only populate the options here - loading is deferred until the
        // user explicitly picks a revision (see handleRevisionChange), so
        // selecting a WBS project alone never triggers a data load.
      })
      .catch((error) => {
        console.log("Failed to load revisions", error);
        if (!cancelled) {
          setRevisions(["0"]);
          setRevisionStamps({});
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedProject?.id]);

  const handleRevisionChange = (_event, value) => {
    if (!value || !selectedProject?.id) return;

    if (value === NEW_REVISION_OPTION) {
      const nextRev = String(Math.max(0, ...revisions.map(Number)) + 1);
      setRevisions((prev) =>
        [...prev, nextRev].sort((a, b) => Number(a) - Number(b)),
      );
      setSelectedModelingRevision(nextRev);
      setRevision(nextRev);
      setConfirmedRevision({
        projectId: selectedProject.id,
        revision: nextRev,
        // Branching off the newest revision: this one opens with its data
        // (see loadPvData), unlike a revision that was only seeded.
        isNew: true,
      });
      return;
    }

    setSelectedModelingRevision(value);
    setRevision(value);
    setConfirmedRevision({ projectId: selectedProject.id, revision: value });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        px: 1.5,
        py: 0.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
      >
        {/* keep this in the left */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <FolderOpenRoundedIcon fontSize="small" />
          </Stack>

          <HeaderField label="Client UID" minWidth={140}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {clientPrefix || clientFullName || "—"}
            </Typography>
            {clientFullName && clientPrefix && (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                title={clientFullName}
                sx={{ display: "block", maxWidth: 200 }}
              >
                {clientFullName}
              </Typography>
            )}
          </HeaderField>

          <HeaderField label="Project# UID">
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              title={plantName}
            >
              {projectCode || "—"}
            </Typography>
            {plantDescription && (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                title={plantDescription}
                sx={{ display: "block", maxWidth: 260 }}
              >
                {plantDescription}
              </Typography>
            )}
          </HeaderField>

          <HeaderField label="WBS UID" minWidth={canEditSelection ? 260 : 160}>
            {canEditSelection ? (
              <LoadProjectDataComboBox
                plantId={plantId}
                clientName={clientName}
                type={type}
                onSelect={onProjectSelect}
              />
            ) : (
              <>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {selectedProject?.name || selectedProject?.link || "—"}
                </Typography>
                {selectedProject?.description && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    title={selectedProject.description}
                    sx={{ display: "block", maxWidth: 260 }}
                  >
                    {selectedProject.description}
                  </Typography>
                )}
              </>
            )}
          </HeaderField>

          {/* While a document tab is open the field reports that document:
              its name on the label, then the revision and the date it was
              saved, then who saved it. Otherwise it is the plain Rev field -
              the revision number on the label, the stamp under it. */}
          <HeaderField
            label={shownEstimationTab ? shownDocumentName : revisionLabel}
            minWidth={150}
          >
            {/* Read-only mode shows no value row: the label already carries
                the revision number. */}
            {canEditSelection && (
              <Autocomplete
                size="small"
                fullWidth
                disabled={!selectedProject}
                options={[...revisions, NEW_REVISION_OPTION]}
                value={displayedRevision}
                getOptionLabel={(option) =>
                  option == null
                    ? ""
                    : option === NEW_REVISION_OPTION
                      ? "+ Add New Revision"
                      : String(option)
                }
                onChange={handleRevisionChange}
                sx={compactComboSx}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select revision" />
                )}
              />
            )}
            {/* Who last saved this revision, and when - blank for a revision
                that has never been saved. */}
            {shownEstimationTab ? (
              <DocumentRevisionStamp
                revisionLabel={revisionLabel}
                stamp={shownSaveStamp}
              />
            ) : (
              <RevisionSaveStamp stamp={shownSaveStamp} />
            )}
          </HeaderField>

          {/* Only while an Estimation tab that is itself a document is open -
              see shownEstimationTab. The client's own reference for that
              document, as the register records it: its number and revision
              on one line ("Client Doc" / "Client Revision" there), its
              description under them. */}
          {shownEstimationTab && (
            <HeaderField label="Client Document Ref">
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                title={`${shownEstimationTab} — ${
                  clientDocumentRef || "no document in the register"
                }`}
              >
                {clientDocumentRef || "—"}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                title={estimationDoc?.description || ""}
                sx={{ display: "block", maxWidth: 220 }}
              >
                {estimationDoc?.description || "—"}
              </Typography>
            </HeaderField>
          )}

          <Button
            className="header-save-btn"
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={saving}
            sx={{ alignSelf: "center", flexShrink: 0 }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Stack>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default function PVMain() {
  const { project, currentTab, setCurrentTab } = React.useContext(PVContext);

  const [selectedProject, setSelectedProject] = React.useState(null);

  // The projects tree owns the WBS record; the header shows a copy of it,
  // fetched when the WBS was opened. An edit there carries what changed,
  // so the header follows it straight away rather than re-reading it.
  useWbsUpdated(selectedProject?.id, ({ projectId, changes }) => {
    setSelectedProject((prev) =>
      prev?.id === projectId ? { ...prev, ...changes } : prev,
    );
  });

  const { plantId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const { setIsDesignTabActive, setSelectedModelingRevision, setRevision } =
    useGeneralContext();
  const { setSelectedProjectId, setConfirmedRevision, confirmedRevision } =
    usePVContext();

  const tabs = pvTabs(project);

  // Nothing renders until a revision is confirmed in the header — the picker
  // itself lives above the tabs, so it stays reachable.
  // Same check the Rev. box uses: a confirmation from a previously selected
  // project must not keep the gate open on the new one.
  const hasRevision = Boolean(
    confirmedRevision && confirmedRevision.projectId === selectedProject?.id,
  );

  const gate = (panel) =>
    hasRevision ? (
      panel
    ) : (
      <Box sx={{ p: 4, width: "100%", textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Select a WBS UID and a revision above to load this project.
        </Typography>
      </Box>
    );

  // Deep link from the DCS sidebar ("Input Data" nodes under a PV project):
  // /plant/:plantId/estimation?projectId=&revision=&projectName= opens
  // straight into that project's saved Input Data revision, the same way
  // picking it manually via the WBS/Rev. dropdowns would.
  // Keyed on the whole link, not just "have we deep-linked once": picking
  // another revision in the tree while this page is already open has to switch
  // the data and the shown revision straight away.
  const appliedDeepLinkRef = React.useRef(null);

  React.useEffect(() => {
    const deepLinkProjectId = searchParams.get("projectId");
    const deepLinkRevision = searchParams.get("revision");
    if (!deepLinkProjectId || deepLinkRevision == null) return;

    const key = `${deepLinkProjectId}|${deepLinkRevision}`;
    if (appliedDeepLinkRef.current === key) return;

    appliedDeepLinkRef.current = key;

    setSelectedProject({
      id: deepLinkProjectId,
      name: searchParams.get("projectName") || "",
    });

    // The link carries only the id and name. The rest of the WBS record -
    // the description shown under WBS UID included - has to be fetched, or
    // the header shows a stub with no description on every tree-driven
    // open (the pickers are read-only while the tree owns selection).
    getProjectById(deepLinkProjectId)
      .then((full) => {
        if (appliedDeepLinkRef.current !== key) return;
        setSelectedProject((prev) =>
          prev?.id === deepLinkProjectId
            ? { ...full, id: deepLinkProjectId, name: prev.name || full?.name || "" }
            : prev,
        );
      })
      .catch(() => {});
    setSelectedProjectId(deepLinkProjectId);
    setSelectedModelingRevision(deepLinkRevision);
    setRevision(deepLinkRevision);
    setConfirmedRevision({
      projectId: deepLinkProjectId,
      revision: deepLinkRevision,
      isNew: searchParams.get("newRevision") === "1",
    });
  }, [
    searchParams,
    setConfirmedRevision,
    setRevision,
    setSelectedModelingRevision,
    setSelectedProjectId,
  ]);

  const navLabel = "pv-modeling";

  const clientName = "DEMO";
  const type = "pv";

  // WBS UID / Rev. in the header are only editable on the Design sub-tab,
  // which only exists inside "Input Data" - leaving that top-level tab
  // (Estimation, Calculation) always makes them read-only.
  React.useEffect(() => {
    if (tabs[currentTab]?.label !== "Input Data") {
      setIsDesignTabActive(false);
    }
  }, [currentTab, tabs, setIsDesignTabActive]);

  React.useEffect(() => {
    if (!tabs.length) return;

    const fullHash = window.location.hash;
    const hashValue = getHashdata(fullHash, navLabel);

    const tabIndex = tabs.findIndex(
      (tab) => tab.label === decodeURIComponent(hashValue),
    );

    if (tabIndex !== -1) {
      setCurrentTab(tabIndex);
    } else if (fullHash) {
      window.location.hash =
        fullHash + "&" + navLabel + "=" + encodeURIComponent(tabs[0].label);
    } else {
      window.location.hash = navLabel + "=" + encodeURIComponent(tabs[0].label);
    }
  }, []);

  // Mount alone isn't enough: coming from the EMS tree straight to an
  // Estimation node while this page is already open changes the hash without
  // remounting, and the sub-tab has to follow. Reads window.location.hash
  // rather than location.hash because handleChange writes it outside the
  // router.
  React.useEffect(() => {
    if (!tabs.length) return;

    const hashValue = getHashdata(window.location.hash, navLabel);
    if (hashValue == null) return;

    const tabIndex = tabs.findIndex(
      (tab) => tab.label === decodeURIComponent(hashValue),
    );
    if (tabIndex !== -1) setCurrentTab(tabIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const handleChange = (event, newValue) => {
    setCurrentTab(newValue);
    setHash(window.location.hash, navLabel, tabs[newValue].label);
  };

  const handleProjectSelect = (selectedProjectData) => {
    console.log("selected PV project", selectedProjectData);
    setSelectedProject(selectedProjectData);

    // If you have a setter inside PVContext, call it here.
    // Example:
    // setProject(selectedProjectData);
    // setModelingData(selectedProjectData?.data);
  };

  return (
    <EdgeMentorHost resolveCodeLabel={resolvePVEdgeMentorCodeLabel}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          p: 0,
        }}
      >
        <ProjectSelectionHeader
          plantId={plantId}
          clientName={clientName}
          type={type}
          selectedProject={selectedProject}
          onProjectSelect={handleProjectSelect}
          activeTabLabel={tabs[currentTab]?.label}
        />

        <Paper
          sx={{
            width: "100%",
            display: "flex",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box sx={{ width: "100%", overflow: "auto" }}>
            <Tabs
              value={currentTab}
              onChange={handleChange}
              orientation="horizontal"
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((tab, i) => (
                <Tab key={tab.label} label={tab.label} value={i} />
              ))}
            </Tabs>

            {tabs[currentTab] && (
              <TabPanelFormat padding={0} {...tabs[currentTab].panelProps}>
                {gate(tabs[currentTab].panel)}
              </TabPanelFormat>
            )}
          </Box>
        </Paper>
      </Box>
    </EdgeMentorHost>
  );
}
